<template>
  <div v-if="assignment">
    <v-container fluid class="content-container">
      <v-layout row wrap>
        <v-flex md6 class="book-top">
          <div class="go-back">
            <i class="material-icons back-icon">arrow_back</i>
          </div>
          <div class="heading then-cover heading-top">Your Assignment</div>
          <card :assignment="assignment" :single="true"></card>
        </v-flex>
        <v-flex md6 class="actions-col">
          <div class="author-message-container">
            <div class="author-message" v-if="assignment.book.author_message">
              {{ assignment.book.author_message }}
            </div>
          </div>
          <div
            class="author-message-sign"
            v-if="assignment.book.author_message"
          >
            <span class="author-name">{{ assignment.book.author }}</span>
          </div>
        </v-flex>
      </v-layout>
    </v-container>
    <v-container fluid class="content-container">
      <v-layout row wrap>
        <v-flex md6 class="first-row">
          <div class="heading then-card">Your Todos</div>

          <template v-if="assignment.status === 'PendingApproval'">
            <div v-if="cantFindReview" class="pending-approval-message">
              <div class="pending-approval-icon">
                <i class="material-icons">check</i>
              </div>
              <div class="pending-approval-text">
                <p>Your review has been turned in. You received your snaps for this assignment immediately upon signing up to read the book in the Library. 
                You can see this in your snap notifications by clicking the bell icon in the top right corner of your dashboard.</p>
                <p>Our automated system will check for your review over the next several days. However, if you'd like to provide us with the direct link to your review to be sure we find it, you can do so by clicking 'Submit Review Link' below."</p>
              </div>
              <div class="amazon-link">
                <view-on-amazon :assignment="assignment"></view-on-amazon>
                <submit-review-link class="submit-review-link" :assignment="assignment"></submit-review-link>
              </div>
              <div class="clearfix"></div>
            </div>
            <div v-else class="pending-approval-message">
              <div class="pending-approval-icon">
                <i class="material-icons">check</i>
              </div>
              <div class="pending-approval-text">
                Your review has been turned in! You've received your snaps for
                your review.
              </div>
              <div class="clearfix"></div>
              <div class="amazon-link">
                <view-on-amazon :assignment="assignment"></view-on-amazon>
              </div>
              <div class="clearfix"></div>
            </div>
          </template>
          <template v-else-if="assignment.status === 'Approved'">
            <div class="pending-approval-message">
              <div class="pending-approval-icon">
                <i class="material-icons">check</i>
              </div>
              <div class="pending-approval-text">
                Your review has been approved and you've received your snaps!
                Nice job!
              </div>
              <div class="clearfix"></div>
              <div class="amazon-link">
                <view-on-amazon :assignment="assignment"></view-on-amazon>
              </div>
              <div class="clearfix"></div>
            </div>
          </template>
          <template v-else-if="assignment.status === 'ActionApproved'">
            <div v-if="assignment.review_issue && assignment.review_issue.request_inform" class="pending-approval-message">
              <div class="pending-approval-icon color-red">
                <i class="material-icons">change_history</i>
              </div>
              <div class="pending-approval-text pending-rejected-text">
                Your review has been reported{{ reportReasonText }}.
                <br />
                <br />
                <span  v-html="assignment.review_issue.reader_message"></span>
                <div>
                </div>
              </div>
              <div class="clearfix"></div>
            </div>
            <div v-else class="pending-approval-message">
              <div class="pending-approval-icon">
                <i class="material-icons">check</i>
              </div>
              <div class="pending-approval-text">
                Thank you for {{ requestedAction }}ing your review! We appreciate your effort in maintaining compliance with Amazon.
                Your snaps and Library access have been restored.
              </div>
              <div class="clearfix"></div>
            </div>
          </template>
          <template v-else-if="assignment.status === 'Rejected'">
            <div class="pending-approval-message pending-rejected">
              <div class="pending-approval-icon">
                <i class="material-icons">change_history</i>
              </div>
              <div class="pending-approval-text pending-rejected-text">
                Our staff has requested changes to your review. Please read our
                note and turn in the review again:
                <br />
                <br />
                <span class="staff-note">{{ assignment.rejection_note }}</span>
              </div>
              <div class="clearfix"></div>
            </div>
            <todo-list :assignment="assignment"></todo-list>
          </template>
          <template v-else-if="assignment.status === 'ActionRequested'">
            <div class="pending-approval-message pending-rejected">
              <div class="pending-approval-icon color-red">
                <i class="material-icons">change_history</i>
              </div>
              <div class="pending-approval-text pending-rejected-text">
                Your review has been reported {{ reportReasonText }}. We have temporarily removed your access to the Library and your earned snaps until you {{ requestedAction }} your review.
                <br />
                <br />
                <span class="staff-note" v-if="assignment.review_issue.status === 'Review'">Thank you. Our team will review your updates and as soon as we confirm they are live on Amazon, your snaps and Library access will be restored!</span>
                <span v-else class="action-description-text" v-html="assignment.review_issue.reader_message"></span>
                <div class="clearfix"></div>
                <div class="amazon-link">
                    <view-review-on-amazon :assignment="assignment"></view-review-on-amazon>
                    <div
                      v-if="assignment.review_issue.status === 'Pending'"
                      class="action turn-in-action second-button"
                      :class="{disable: lockConfirmButton}"
                      @click="reportActionTaken"
                    >
                      I've {{ requestedActionPastTense }} my review!
                    </div>
                    <div class="clearfix"></div>
                </div>
              </div>
              <div class="clearfix"></div>
            </div>
          </template>
          <template v-else-if="assignment.status === 'CouldNotVerify'">
            <div class="pending-approval-message pending-rejected">
              <div class="pending-approval-icon">
                <i class="material-icons">change_history</i>
              </div>
              <div class="pending-approval-text pending-rejected-text">
                Your review was turned in, but we were unable to find it on Amazon. 
                <br>
                The snaps you earned for this assignment were removed. 
                <br>However, if you see your review live on Amazon, you can provide us with the direct link to your review below and we will return your snaps. Click 'Submit Review Link' below to provide your link.
              </div>
              <div class="clearfix"></div>
            </div>
              <view-on-amazon :assignment="assignment"></view-on-amazon>
              <submit-review-link class="submit-review-link" :assignment="assignment"></submit-review-link>
          </template>
          <template v-else>
            <template v-if="assignment.status === 'Archived'">
              <div class="pending-approval-message pending-rejected">
                <div class="pending-approval-icon">
                  <i class="material-icons">change_history</i>
                </div>
                <div class="pending-approval-text pending-rejected-text">
                  Our staff was unable to verify your review on Amazon.
                  Please confirm that you posted your review, and if so, please
                  try reposting your review for this book on Amazon. If you
                  need more help, click the chat button in the bottom left
                  corner of your screen to talk to our team. Thank you!
                </div>
                <div class="clearfix"></div>
              </div>
            </template>

            <div v-if="assignment.is_verified" class="verified-important">
              <p>
                <span class="important-color"
                  >This assignment requires you to purchase the book on
                  Amazon</span
                >.
              </p>
              <p>
                <span class="strong">
                  You
                  <span class="not-word">cannot</span> use Kindle Unlimited to
                  purchase this book for free
                </span>
                because Amazon does not include those reviews as 'Verified
                Purchases'. If you wish not to purchase this book, click 'Cancel
                Assignment' on this page.
              </p>
              <p>
                Amazon may not approve your review if you post it immediately
                after purchasing the book. We recommend waiting at least
                <span class="strong">6 hours</span> after purchasing the book
                before posting your review.
              </p>
            </div>
            <todo-list :assignment="assignment"></todo-list>

            <assistant
              :assignment="assignment"
              v-if="
                assignment &&
                [
                  728, 738, 726, 733, 796, 775, 793, 793, 765, 805, 812, 761,
                  848, 775, 765, 765, 858, 858, 858, 881, 886, 885,
                ].includes(assignment.assignee_id)
              "
            ></assistant>
          </template>
        </v-flex>
        <v-flex
          md6
          v-if="
            assignment.status !== 'PendingApproval' &&
            assignment.status !== 'Approved' &&
            assignment.status !== 'ActionRequested' &&
            assignment.status !== 'ActionApproved' &&
            assignment.status !== 'CouldNotVerify'
          "
        >
          <div class="heading then-card">Your Schedule</div>
          <div class="due-section">
            <div class="due-heading">
              <div class="due-text">
                Your review is due on
                <span
                  class="date"
                  :class="{ speedy: assignment.upgrade_type === 'Speedy' }"
                  >{{ assignment.due_at | shortDate }}</span
                >.
              </div>
            </div>
            <div class="due-calendar">
              <calendar :dueDate="dueDate"></calendar>
            </div>
          </div>
          <div
            class="action cancel-assignment"
            @click="$store.commit('showCancelAssignment')"
          >
            Cancel Assignment
          </div>
          <div class="cancel-assignment-more">
            You can cancel this assignment if you decide you would rather not
            leave a review for any reason.
          </div>
          <cancel-assignment-modal
            :assignment="assignment"
            v-if="showCancelAssignment"
          ></cancel-assignment-modal>

          <!-- <review-list :reviews="otherReviews" hideSummary="true"></review-list> -->
        </v-flex>
      </v-layout>
    </v-container>

    <book-summary :assignment="assignment"></book-summary>
  </div>
</template>

<script>
import Back from "@/modules/Back";
import Bus from "@/modules/Bus";
import Finagle from "@/modules/Finagle";
import Heading from "@/components/Page/Heading";
import Card from "@/components/Assignment/Card";
import Actions from "@/components/Assignment/Actions";
import Summary from "@/components/Assignment/Summary";
import ReviewList from "@/components/Review/List";
import TodoList from "@/components/Assignment/TodoList";
import Calendar from "@/components/Assignment/Calendar";
import CancelAssignment from "@/components/Page/CancelAssignment";
import Assistant from "@/components/Assignment/Assistant/Assistant";
import ViewOnAmazon from "@/components/Assignment/Actions/ViewOnAmazon";
import ViewReviewOnAmazon from "@/components/Assignment/Actions/ViewReviewOnAmazon";
import ReviewLinkSubmission from '../components/Assignment/ReviewLinkSubmission';

import { mapState } from "vuex";
import SubmitReviewLink from '../components/Assignment/Actions/SubmitReviewLink.vue';


export default {
  name: "AssignmentPage",
  components: {
    Heading,
    Card,
    Actions,
    TodoList,
    Calendar,
    ReviewList,
    "book-summary": Summary,
    "cancel-assignment-modal": CancelAssignment,
    Assistant,
    ViewOnAmazon,
    ViewReviewOnAmazon,
    ReviewLinkSubmission,
    SubmitReviewLink,
  },
  data: function () {
    return {
      assignment: null,
      otherReviews: [],
      lockConfirmButton: false,
    };
  },
  computed: {
    ...mapState({
      showCancelAssignment: "showCancelAssignment",
    }),
    cantFindReview() {
      return true;
      console.log(this.assignment)
      if (!this.assignment.status_messages) {
        return false
      }
      return this.assignment.status_messages.verifying
    },
    dueDate() {
      if (this.assignment.due_at) {
        this.assignment.due_at = this.assignment.due_at.replace(/-/g, "/");
      }
      if (this.assignment.proposed_due_at) {
        this.assignment.proposed_due_at =
          this.assignment.proposed_due_at.replace(/-/g, "/");
      }
      return new Date(this.assignment.due_at);
    },
    minTurnaroundTime() {
      // console.log("words: ",this.assignment.book.word_count)
      const words = this.assignment.book.word_count //(this.assignment.points - 1000) / 0.005

      const hrsToRead = Math.ceil(words / 600) / 60

      if (hrsToRead < 0.5) {
        return 30 * 60 * 1000
      } else {
        return hrsToRead * 60 * 60 * 1000
      }
    },
    reportReasonText() {
      if (!this.assignment.review_issue || this.assignment.review_issue.issue.label === 'Other') {
        return ''
      }
      return " as " + this.assignment.review_issue.issue.label
    },
    requestedAction() {
      if (this.assignment.review_issue && this.assignment.review_issue.request_edit) {
        return "edit"
      }
      return "delete"
    },
    requestedActionPastTense() {
      if (this.assignment.review_issue && this.assignment.review_issue.request_edit) {
        return "edited"
      }
      return "deleted"
    }
  },
  methods: {
    async loadData() {
      this.assignment = await Back.assignment(this.$route.params.id);
      const otherAssignments = await Back.assignments({
        book_id: this.assignment.book.id,
      });
// console.log(this.assignment)
      if (this.assignment.due_at) {
        this.assignment.due_at = this.assignment.due_at.replace(/-/g, "/");
      }
      if (this.assignment.proposed_due_at) {
        this.assignment.proposed_due_at =
          this.assignment.proposed_due_at.replace("-", "/");
      }
      // console.log("created and edited", this.assignment);

      // console.log("otherAssignments");
      // console.log(otherAssignments);
      this.otherReviews = Finagle.gatherApprovedReviews(otherAssignments);
      // console.log(this.otherAssignments);

      // console.log("assigment!", this.assignment);
      this.checkTurnaroundTime()
    },
    checkTurnaroundTime(){
      //Verify the user has waited the defined amount of time before they can submit their review
      const now = new Date()
      const assignmentDate = new Date(
        this.assignment.started_at.replace(/-/g, "/")
        );
        const startTime = assignmentDate.getTime() - assignmentDate.getTimezoneOffset() * 60 * 1000;

      const earliestTurninDate = new Date(Math.round(
        (startTime + this.minTurnaroundTime)/(60*1000))*60*1000
      );
      const options = {hour: "numeric",minute: "numeric"}
      let waitTimeString = ""
      let hours = Math.floor(this.minTurnaroundTime / (1000 * 60 * 60));
      let minutes = Math.floor((this.minTurnaroundTime % (1000 * 60 * 60))/(1000 * 60));

      if (hours && minutes > 0 && minutes < 10) {
        waitTimeString = hours + ":0" + minutes + " hours";
      } else if (hours && minutes > 0 && minutes >= 10) {
        waitTimeString = hours + ":" + minutes + " hours";
      } else if (hours===1) {
        waitTimeString = hours + " hour"
      } else if (hours) {
        waitTimeString = hours + " hours"
      } else waitTimeString = minutes + " minutes"
      if (earliestTurninDate <= now) {
        console.log("timecheck passed")
      } else {
        console.log("timecheck failed")
      }
        // console.log("timeToRead: ", waitTimeString)
        // console.log("startDate: ",assignmentDate)
        // console.log("startTime: ",new Date(startTime))
        // console.log("earliestTurnIn: ", earliestTurninDate)
        // console.log("now: ", now)
    },
    async reportActionTaken() {
      this.lockConfirmButton = true
      await Back.reportActionTaken(this.assignment.review_issue.id)
      .then(response => {
          this.lockConfirmButton = false;
          this.assignment = response
          if (this.assignment.due_at) {
            this.assignment.due_at = this.assignment.due_at.replace(/-/g, "/");
          }
          if (this.assignment.proposed_due_at) {
            this.assignment.proposed_due_at =
              this.assignment.proposed_due_at.replace("-", "/");
          }
          this.checkTurnaroundTime()
      })
    },
  },
  async created() {
    await this.loadData();
    Bus.$on("UserChanged", async () => {
      await this.loadData();
    });
    // Bus.$on("AssignmentChanged", (assignment) => {
    //   console.log(assignment)
    //   this.assignment = assignment
    // })
  },
};
</script>

<style lang="scss" scoped>
.page-heading {
  display: flex;
  background: #fff;
  border-top: 0px solid #ddd;
  border-bottom: 1px solid #ddd;
  height: 72px;
  margin-top: 0px;
  margin-bottom: 12px;

  .page-icon {
    padding: 18px 10px 12px 42px;
  }
  .page-icon-img {
    width: 38px;
  }
  .page-title {
    padding: 21px 0 0 0px;
    font-family: $font-family;
    font-size: 17pt;
    font-weight: 600;
    color: darken(#25ba61, 10%);
  }
}

.head-content {
  padding: 0;
  padding-top: 0px;
}
.actions-col {
  padding-top: 30px;
  padding-left: 0px;
}

.author-message-sign {
  text-align: left;
  padding: 24px 0 0 38px;
  font-weight: 500;
  font-size: 15pt !important;
  font-family: "Source Sans Pro";
  color: #222;
}

.is-waiting {
  text-align: left;
  padding: 38px 0 0 68px;
  font-weight: 500;
  font-size: 15pt !important;
  font-family: "Source Sans Pro";
  color: #222;
}

.turn-in-action {
  margin: 0;
  margin-top: -6px;
  font-size: 10.5pt !important;
  background: lighten(#c8e6c9, 5%);
  color: #43a047;
}

.second-button {
  margin-left: 10px;
}

.due-section {
  display: flex;
  flex-direction: column;
}
.due-heading {
  display: flex;

  .due-icon {
    display: none;
    padding-top: 4px;
  }
  .due-icon-i {
    color: #2e7d32;
  }
  .due-text {
  }
  .due-text-date {
    font-weight: 700;
    color: #2e7d32;
  }
}
.due-calendar {
  margin-left: 8px;
}

.due-heading {
  display: none;
  text-align: left;
  padding: 0px 0 0 82px;
  font-weight: 500;
  font-size: 15pt !important;
  font-family: "Source Sans Pro";
  color: #fff;
}

.author-message-container {
  max-width: 560px;
  margin-top: 28px;
  margin-left: 0px;
}
.author-message {
  position: relative;
  border-radius: 0.4em;
  background: lighten(#e3f2fd, 1%);
  border-radius: 16px;
  padding: 16px 20px;
  font-size: 13.5pt;
  font-weight: 500;
  line-height: 1.35em;
  color: #222;
  font-family: "Source Sans Pro";
}
.author-message:after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 12%;
  width: 0;
  height: 0;
  border: 20px solid transparent;
  border-top-color: lighten(#e3f2fd, 1%);
  border-bottom: 0;
  border-left: 0;
  margin-left: -10px;
  margin-bottom: -16px;
}
.author-name {
  font-weight: 700;
  color: darken(#ff8a65, 5%);
}
.due-icon {
  font-size: pt;
}
.section-divider {
  width: 100%;
  height: 40px;
}

.verified-important {
  margin-bottom: 16px;
  border: 1px solid #e3e3e3;
  border-radius: 8px;
  padding: 16px 20px;
  font-size: 13pt;
  color: #111;
  font-family: $font-family;
}
.strong {
  font-weight: 600;
}
.important-color {
  font-weight: 600;
  color: darken(#e08d00, 6%);
}
.not-word {
  font-weight: 600;
  text-decoration: underline;
}

.date {
  font-weight: 600;
  color: #fff;
}
.date.speedy {
  color: lighten(#ef6c00, 12%);
}

.cancel-assignment {
  margin: 10px 0 0 26px;
  color: #999;
  background: #f2f2f2;
  font-size: 11pt !important;
  cursor: pointer;
}
.cancel-assignment:hover {
  color: #666;
}
.cancel-assignment-more {
  font-size: 12pt;
  color: #111;
  padding: 10px 30px 10px 30px;
  max-width: 500px;
  font-family: "Source Sans Pro";
}

.pending-approval-message {
  color: darken(#46a74a, 6%);
  font-size: 14.5pt;
  padding: 20px 20px;
  font-family: $font-family;
}
.pending-rejected {
  display: flex;
  color: #555 !important;
  padding-bottom: 22px;
}
.pending-approval-icon {
  float: left;
  font-size: 20pt;
  padding: 12px 20px;
}
.color-red {
    color: $color-red;
  }
.pending-approval-text {
  padding-top: 13px;
  color: darken(#46a74a, 6%);
  font-size: 14.5pt;
  font-family: $font-family;
}
.pending-rejected-text {
  padding-top: 14px !important;
  color: #555 !important;
}
.staff-note {
  color: darken(#b59505, 0%);
  font-weight: 600;
}

.action-description-text {
  font-weight: 600;
}

.button-container {
  margin-top: 24px;
}
.amazon-link {
  margin-top:10px;
  padding-left:62px;
}
.submit-review-link {
  display: inline;
  padding-left: 1em;
}
.patron-card {
  box-shadow: 0px 1px 0.5rem rgba(116, 75, 75, 0.25);
  border-radius: 12px;
  margin-bottom: 30px;
  padding: 24px;
  font-family: $font-family;
}
.patron-heading {
  font-size: 13pt;
  font-weight: 600;
  color: #7e57c2;
}
</style>